// @ts-check

/**
 * Renders the fixed navigation bar and mobile menu.
 */
export class Nav {
  /**
   * @param {HTMLElement} container
   * @param {Object} [options={}]
   * @param {string} [options.logo='Cast in Exile']
   * @param {Array<{id: string, label: string}>} [options.links=[]]
   */
  constructor(container, options = {}) {
    this.container = container;
    this.logo = options.logo ?? 'Cast in Exile';
    this.links = options.links ?? [
      { id: 'hero', label: 'Home' },
      { id: 'runway', label: 'Works' },
      { id: 'about', label: 'About' },
      { id: 'contact', label: 'Contact' },
    ];

    this.isOpen = false;
    this.isScrolled = false;
    this.currentSection = 'hero';
    this.menuButton = null;
    this.linksList = null;
    this.linkElements = [];
    /** Whether this nav instance owns the body scroll lock (avoids racing the lightbox). */
    this.ownsScrollLock = false;
    /** rAF handle for scroll throttling — prevents layout thrash. */
    this._scrollRaf = 0;

    this.render();
    this.bindEvents();
    this.publishHeight();
  }

  /**
   * Publishes the bar's own height as `--nav-h` on the root.
   *
   * The bar is fixed, so nothing in the flow knows how much of the top of the
   * screen it owns — and a hall header only has a *floor* to guess with.
   * That floor (64px) was smaller than the bar on a phone (73px), so the
   * kicker of every hall title sat underneath the bar and the heading read as
   * incomplete. Measuring it here keeps the two in step at any type size.
   */
  publishHeight() {
    const apply = () => {
      const h = this.container.getBoundingClientRect().height;
      if (h > 0) {
        document.documentElement.style.setProperty('--nav-h', `${Math.round(h)}px`);
      }
    };
    apply();
    // The bar's height changes with --type (a resize or an orientation flip),
    // so re-measure rather than measuring once.
    if (typeof ResizeObserver === 'function') {
      this._navObserver = new ResizeObserver(apply);
      this._navObserver.observe(this.container);
    } else {
      window.addEventListener('resize', apply, { passive: true });
      this._navResize = apply;
    }
  }

  /**
   * Builds the DOM structure for the navigation bar.
   */
  render() {
    this.container.innerHTML = '';

    const logo = document.createElement('a');
    logo.className = 'nav-logo';
    logo.href = '#hero';
    logo.textContent = this.logo;
    logo.setAttribute('aria-label', '返回首页');

    this.linksList = document.createElement('ul');
    this.linksList.className = 'nav-links';
    this.linksList.setAttribute('role', 'menubar');

    this.linkElements = this.links.map((link) => {
      const item = document.createElement('li');
      item.setAttribute('role', 'none');

      const anchor = document.createElement('a');
      anchor.className = 'nav-link';
      anchor.href = `#${link.id}`;
      anchor.textContent = link.label;
      anchor.setAttribute('role', 'menuitem');
      anchor.dataset.section = link.id;

      item.appendChild(anchor);
      this.linksList.appendChild(item);
      return anchor;
    });

    this.menuButton = document.createElement('button');
    this.menuButton.className = 'nav-toggle';
    this.menuButton.setAttribute('aria-label', '切换导航菜单');
    this.menuButton.setAttribute('aria-expanded', 'false');
    this.menuButton.type = 'button';
    this.menuButton.innerHTML = `
      <span class="nav-toggle-bar" aria-hidden="true"></span>
      <span class="nav-toggle-bar" aria-hidden="true"></span>
    `;

    this.container.appendChild(logo);
    this.container.appendChild(this.linksList);
    this.container.appendChild(this.menuButton);
  }

  /**
   * Attaches scroll and click event listeners.
   */
  bindEvents() {
    window.addEventListener('scroll', () => this.onScroll(), { passive: true });

    this.menuButton?.addEventListener('click', () => this.toggleMenu());

    this.linkElements.forEach((link) => {
      link.addEventListener('click', () => this.closeMenu());
    });
  }

  /**
   * Updates the navigation background and active link based on scroll position.
   * Throttled via rAF to avoid layout thrash from getBoundingClientRect.
   */
  onScroll() {
    if (this._scrollRaf) return;
    this._scrollRaf = requestAnimationFrame(() => {
      this._scrollRaf = 0;
      const scrollY = window.scrollY;
      const wasScrolled = this.isScrolled;
      this.isScrolled = scrollY > 50;

      if (wasScrolled !== this.isScrolled) {
        this.container.classList.toggle('is-scrolled', this.isScrolled);
      }

      this.updateActiveSection();
    });
  }

  /**
   * Highlights the link corresponding to the current visible section.
   */
  updateActiveSection() {
    const sections = this.links.map((link) => document.getElementById(link.id));
    // Keep "Home" active through the first viewport so the nav doesn't flip to
    // "Works" while the hero (and its fixed text layer) is still on screen.
    if (window.scrollY < window.innerHeight * 0.7) {
      if (this.currentSection !== 'hero') {
        this.currentSection = 'hero';
        this.linkElements.forEach((link) => {
          link.classList.toggle('is-active', link.dataset.section === this.currentSection);
        });
      }
      return;
    }

    let closest = this.links[0]?.id ?? 'hero';
    let minDistance = Infinity;

    sections.forEach((section, index) => {
      if (!section) return;
      const rect = section.getBoundingClientRect();
      const distance = Math.abs(rect.top - 80);
      if (distance < minDistance) {
        minDistance = distance;
        closest = this.links[index].id;
      }
    });

    if (this.currentSection === closest) return;
    this.currentSection = closest;

    this.linkElements.forEach((link) => {
      link.classList.toggle('is-active', link.dataset.section === this.currentSection);
    });
  }

  /**
   * Toggles the mobile navigation menu.
   */
  toggleMenu() {
    this.isOpen = !this.isOpen;
    this.menuButton?.setAttribute('aria-expanded', String(this.isOpen));
    this.linksList?.classList.toggle('is-open', this.isOpen);
    this.container.classList.toggle('is-open', this.isOpen);
    // Only manage the scroll lock if the lightbox isn't currently holding it.
    if (this.isOpen) {
      if (document.body.style.overflow !== 'hidden') {
        document.body.style.overflow = 'hidden';
        this.ownsScrollLock = true;
      }
    } else if (this.ownsScrollLock) {
      document.body.style.overflow = '';
      this.ownsScrollLock = false;
    }
  }

  /**
   * Closes the mobile navigation menu.
   */
  closeMenu() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.menuButton?.setAttribute('aria-expanded', 'false');
    this.linksList?.classList.remove('is-open');
    this.container.classList.remove('is-open');
    if (this.ownsScrollLock) {
      document.body.style.overflow = '';
      this.ownsScrollLock = false;
    }
  }

  /**
   * Removes scroll listener and closes menu if open.
   */
  destroy() {
    this.closeMenu();
    this._navObserver?.disconnect();
    this._navObserver = null;
    if (this._navResize) {
      window.removeEventListener('resize', this._navResize);
      this._navResize = null;
    }
    // Scroll listener is passive and on window — GC handles it on navigation.
  }
}
